<script setup lang="ts">
import { useI18n } from "vue-i18n";
import DocumentVerticalDTO = App.Data.DocumentVerticalDTO;
import DocumentDimensionDTO = App.Data.DocumentDimensionDTO;
import { useDragAndDrop } from "@/ui/composables/useDragAndDrop";
import { computed, ref } from "vue";
const { t } = useI18n();
const attributes = defineModel<DocumentVerticalDTO[] | DocumentDimensionDTO[]>(
	"items"
);
import DataTableDO = App.Data.Explorer.DataTableDO;

const { dataTable, metricsChanged } = defineProps<{
	dataTable: DataTableDO;
	metricsChanged: boolean;
}>();

const hasHiddenColumns = computed(() => {
	return dataTable.hidden_columns.length > 0;
});

const columns = computed(() =>
	(attributes.value ?? []).map((item) => ({
		title: t(`properties.${item.name_clean}.title`),
	}))
);

const { dragstartHandle, dragoverHandle, dragleaveHandle, dragdropHandle } =
	useDragAndDrop(attributes);

const draggedIndex = ref<number | null>(null);

const startDrag = (event: DragEvent, index: number) => {
	draggedIndex.value = index;
	dragstartHandle(event, index);
};

const indexForGap = (gap: number): number =>
	draggedIndex.value !== null && gap > draggedIndex.value ? gap - 1 : gap;

const finishDrag = (event: DragEvent, gap: number) => {
	dragdropHandle(event, indexForGap(gap));
	draggedIndex.value = null;
};
</script>

<template>
	<div class="order d-flex flex-column ga-2">
		<h6 class="zone-label">{{ t("axisDialog.order.heading") }}</h6>

		<v-alert
			v-if="metricsChanged && hasHiddenColumns"
			type="warning"
			density="compact"
			class="text-caption"
		>
			{{ t("axisDialog.order.hiddenColumnsReset") }}
		</v-alert>

		<v-alert
			v-if="columns.length === 0"
			type="error"
			density="compact"
			class="text-caption"
		>
			{{ t("axisDialog.order.empty") }}
		</v-alert>
		<template v-else>
			<p class="text-caption muted mb-0">{{ t("axisDialog.order.hint") }}</p>
			<div class="nodes">
				<div
					class="node"
					draggable="true"
					v-for="(column, index) in columns"
					@dragstart="(ev) => startDrag(ev, index)"
					:key="index"
				>
					<v-icon
						class="grip"
						icon="fa: fa-regular fa-grip-dots-vertical"
						size="14"
					/>
					<span class="node-label text-body-2">{{ column.title }}</span>
					<span
						class="order-dropzone order-dropzone--before"
						@dragover="(ev) => dragoverHandle(ev, indexForGap(index))"
						@dragleave="dragleaveHandle"
						@drop="(ev) => finishDrag(ev, index)"
					>
					</span>
					<span
						v-if="index === columns.length - 1"
						class="order-dropzone order-dropzone--after"
						@dragover="(ev) => dragoverHandle(ev, indexForGap(columns.length))"
						@dragleave="dragleaveHandle"
						@drop="(ev) => finishDrag(ev, columns.length)"
					>
					</span>
					<button
						type="button"
						class="remove"
						v-tooltip="t('axisDialog.order.remove')"
						:aria-label="t('axisDialog.order.remove')"
						data-testid="delete-column-button"
						@click="attributes.splice(index, 1)"
					>
						<v-icon icon="fa: fa-regular fa-xmark" size="12" />
					</button>
				</div>
			</div>
		</template>
	</div>
</template>

<style scoped lang="scss">
.order {
	min-width: 0;
}

/* Matches the FilterDialog zone labels. */
.zone-label {
	font-size: 0.75rem;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: rgb(var(--color-on-background));
	margin-bottom: 0;
}

.muted {
	color: rgb(var(--color-on-background));
}

.nodes {
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
}

.node {
	position: relative;
	display: flex;
	align-items: center;
	gap: 0.375rem;
	min-height: 2.125rem;
	padding-inline: 0.375rem 0.25rem;
	border-radius: 4px;
	/* Also the backdrop of the drag image, which is a clone of this row. */
	background: rgb(var(--color-background));
	user-select: none;
	cursor: grab;
	transition: background-color 125ms ease;

	&:hover {
		background: rgb(var(--color-secondary));
	}
}

.grip {
	flex: 0 0 auto;
	color: rgb(var(--color-on-background));
	cursor: grab;
}

.node-label {
	flex: 1 1 auto;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.remove {
	display: flex;
	flex: 0 0 auto;
	align-items: center;
	justify-content: center;
	width: 1.25rem;
	height: 1.25rem;
	border-radius: 4px;
	color: rgb(var(--color-on-background));
	cursor: pointer;

	&:hover {
		color: rgb(var(--color-error));
	}
}

.order-dropzone {
	position: absolute;
	left: 0;
	width: 100%;
	height: 0.75rem;
	z-index: 100;
	border: 1px dashed transparent;
	transition: all 125ms ease;

	&--before {
		top: -0.375rem;
	}

	&--after {
		bottom: -0.375rem;
	}

	&.over {
		border-color: rgb(var(--color-primary));
		background: rgba(var(--color-primary), 0.1);
	}
}
</style>
